import { useTranslation } from 'react-i18next';
import { templatesResponseSchema, type CatalogItem, type FormTemplate } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { newLine, type Line } from './LineEditor.js';

const input =
  'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none';

/** A template's lines as editor lines; items the catalog no longer has are skipped. */
export function linesFromTemplate(template: FormTemplate, items: CatalogItem[]): Line[] {
  const byId = new Map(items.map((i) => [i.id, i]));
  const lines: Line[] = [];
  for (const l of template.lines) {
    const item = byId.get(l.catalogItemId);
    if (item) lines.push(newLine(item, { quantity: l.quantity, condition: l.condition, notes: l.notes ?? '' }));
  }
  return lines;
}

/**
 * "Start from a template" (owner, 2026-09-13): picking one replaces the
 * current lines with the template's; serialized lines then wait for a serial
 * in the single-issue wizard, and get one unit each in bulk issue.
 */
export function TemplateSelect({ items, onPick }: { items: CatalogItem[]; onPick: (lines: Line[]) => void }) {
  const { t } = useTranslation();
  const templates = useApi('/templates', templatesResponseSchema);
  if (templates.kind !== 'ready' || templates.data.templates.length === 0) return null;

  return (
    <label className="text-xs text-slate-600">
      {t('templates.startFrom')}
      <select
        className={`${input} mt-1 block`}
        value=""
        onChange={(e) => {
          const tpl = templates.data.templates.find((x) => x.id === e.target.value);
          if (tpl) onPick(linesFromTemplate(tpl, items));
        }}
        data-testid="template-select"
      >
        <option value="">{t('templates.choose')}</option>
        {templates.data.templates.map((tpl) => (
          <option key={tpl.id} value={tpl.id}>
            {tpl.name} · {t('forms.itemCount', { count: tpl.lines.length })}
          </option>
        ))}
      </select>
    </label>
  );
}
